// Filas & Chatbot (/queues) — mockup "Tela Aeros Dark - Filas e Chatbot":
// header 60px com busca e "Adicionar fila", faixa de KPIs e card de tabela em
// CSS grid com alça de arraste para reordenar o menu do bot.
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import ScheduleIcon from "@material-ui/icons/Schedule";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import MainContainer from "../../components/MainContainer";
import QueueModal from "../../components/QueueModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useQueuePageStyles, { TABLE_GRID } from "./queuePageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";
import {
  scheduleSummary,
  queueSubtitle,
  botLabel,
  kpiTotals,
  reorderQueues,
  orderPayload,
} from "./queueRules";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    // A listagem já vem ordenada por `orderQueue`; substituir inteiro (em vez
    // de mesclar) mantém a ordem do servidor como fonte da verdade.
    return [...action.payload];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const idx = state.findIndex((q) => q.id === queue.id);
    if (idx !== -1) {
      // O socket manda a fila sem as contagens (elas só vêm da listagem);
      // preservar as que já estão em tela evita o card zerar "6 atendentes"
      // depois de uma simples troca de nome.
      const anterior = state[idx];
      const novo = [...state];
      novo[idx] = {
        userCount: anterior.userCount,
        openCount: anterior.openCount,
        pendingCount: anterior.pendingCount,
        chatbotCount: anterior.chatbotCount,
        ...queue,
      };
      return novo;
    }
    return [...state, queue];
  }

  if (action.type === "DELETE_QUEUE") {
    return state.filter((q) => q.id !== action.payload);
  }

  return state;
};

const Queues = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });
  const classes = useQueuePageStyles();
  const theme = useTheme();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue");
      dispatch({ type: "LOAD_QUEUES", payload: data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const onQueueEvent = (data) => {
      // "reorder" traz a lista inteira já renumerada pelo servidor.
      if (data.action === "reorder" && Array.isArray(data.queues)) {
        dispatch({ type: "LOAD_QUEUES", payload: data.queues });
        return;
      }
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);
    return () => socket.off(`company-${companyId}-queue`, onQueueEvent);
  }, [socket, companyId]);

  const totais = useMemo(() => kpiTotals(queues), [queues]);

  // A busca é local: a listagem de filas é curta e vem inteira em uma
  // requisição, então filtrar no cliente evita ida ao servidor a cada tecla.
  const visiveis = useMemo(() => {
    const termo = searchParam.trim().toLowerCase();
    if (!termo) return queues;
    return queues.filter((q) => (q.name || "").toLowerCase().includes(termo));
  }, [queues, searchParam]);

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/queue/${selected.id}`);
      toast.success(i18n.t("aeros.queues.delete.done"));
      setConfirmOpen(false);
      setSelected(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  // Arrastar reordena na tela na hora e grava a ordem inteira de uma vez;
  // gravar fila a fila deixaria a numeração pela metade se algo falhasse.
  const handleDrop = async (destino) => {
    const origem = dragIndex;
    setDragIndex(null);
    setOverIndex(null);
    if (origem === null || origem === destino) return;
    // Reordenar durante uma busca moveria a fila para a posição errada na
    // lista completa; a alça fica desabilitada com filtro ativo.
    if (searchParam.trim()) return;

    const anterior = queues;
    const nova = reorderQueues(queues, origem, destino);
    dispatch({ type: "LOAD_QUEUES", payload: nova });

    try {
      await api.put("/queue/order", { order: orderPayload(nova) });
    } catch (err) {
      dispatch({ type: "LOAD_QUEUES", payload: anterior });
      toastError(err);
    }
  };

  const podeArrastar = !searchParam.trim();

  const renderRow = (queue, index) => {
    const cor = queue.color || "#7c7c7c";
    const horario = scheduleSummary(queue.schedules);
    const temBot = Number(queue.chatbotCount) > 0;
    return (
      <div
        key={queue.id}
        className={`${c.tableRow} ${dragIndex === index ? classes.dragging : ""} ${
          overIndex === index && dragIndex !== index ? classes.dropTarget : ""
        }`}
        draggable={podeArrastar}
        onDragStart={() => setDragIndex(index)}
        onDragOver={(e) => {
          e.preventDefault();
          setOverIndex(index);
        }}
        onDrop={() => handleDrop(index)}
        onDragEnd={() => {
          setDragIndex(null);
          setOverIndex(null);
        }}
      >
        <Tooltip title={podeArrastar ? i18n.t("aeros.queues.dragHandle") : ""}>
          <span className={classes.dragHandle}>
            <DragIndicatorIcon />
          </span>
        </Tooltip>

        <div className={`${c.tableCell} ${classes.idCell}`}>#{queue.id}</div>

        <div className={classes.nameCell}>
          <span
            className={classes.tile}
            style={{
              backgroundColor: alpha(cor, 0.14),
              color: stateText(theme, cor, ""),
            }}
          >
            <AccountTreeIcon />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={classes.queueName} title={queue.name}>
              {queue.name}
            </div>
            <div className={classes.queueSub}>{queueSubtitle(queue)}</div>
          </div>
        </div>

        <div className={classes.colorCell}>
          <span className={classes.swatch} style={{ backgroundColor: cor }} />
          <span className={classes.colorHex}>{cor}</span>
        </div>

        <div className={c.tableCell}>
          <span className={classes.orderBadge}>{queue.orderQueue ?? "—"}</span>
        </div>

        <div
          className={`${classes.greetingCell} ${
            queue.greetingMessage ? "" : classes.greetingEmpty
          }`}
          title={queue.greetingMessage}
        >
          {queue.greetingMessage || i18n.t("aeros.queues.noGreeting")}
        </div>

        <div className={classes.botCell}>
          <span
            className={`${classes.botPill} ${temBot ? "" : classes.botPillOff}`}
          >
            {temBot && <SmartToyIcon />}
            {botLabel(queue.chatbotCount)}
          </span>
          <span
            className={`${classes.schedLine} ${
              horario.always ? classes.schedAlways : ""
            }`}
          >
            <ScheduleIcon />
            {horario.label}
          </span>
        </div>

        <div className={c.cellActions}>
          <Tooltip title={i18n.t("aeros.queues.actions.edit")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.queues.actions.edit")}
              onClick={() => {
                setSelected(queue);
                setModalOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.queues.actions.delete")}>
            <IconButton
              className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
              aria-label={i18n.t("aeros.queues.actions.delete")}
              onClick={() => {
                setSelected(queue);
                setConfirmOpen(true);
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  const deleteDescription = () => {
    if (!selected) return "";
    const abertos = Number(selected.openCount) || 0;
    return abertos > 0
      ? i18n.t("aeros.queues.delete.bodyOpen", { count: abertos })
      : i18n.t("aeros.queues.delete.body");
  };

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.queues.title")}
            <span className={c.pageTitleCount}> ({queues.length})</span>
          </div>
          <div className={classes.pageSubtitle}>
            {i18n.t("aeros.queues.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.queues.search")}
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.queues.search") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelected(null);
              setModalOpen(true);
            }}
          >
            {i18n.t("aeros.queues.add")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.kpiGrid}>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.queues.kpi.queues")}
            </div>
            <div className={classes.kpiValue}>{totais.total}</div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.queues.kpi.queuesHint", { count: totais.withBot })}
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={classes.kpiLabel}>
              {i18n.t("aeros.queues.kpi.pending")}
            </div>
            <div
              className={classes.kpiValue}
              style={{ color: stateText(theme, AEROS.warning, "warning") }}
            >
              {totais.pending}
            </div>
            <div className={classes.kpiHint}>
              {i18n.t("aeros.queues.kpi.pendingHint")}
            </div>
          </div>
        </div>

        <div className={c.tableCard}>
          <div className={c.tableHeadRow}>
            <div className={c.tableHeadCell} />
            <div className={c.tableHeadCell}>{i18n.t("aeros.queues.table.id")}</div>
            <div className={c.tableHeadCell}>{i18n.t("aeros.queues.table.name")}</div>
            <div className={c.tableHeadCell}>{i18n.t("aeros.queues.table.color")}</div>
            <div className={c.tableHeadCell}>{i18n.t("aeros.queues.table.order")}</div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.queues.table.greeting")}
            </div>
            <div className={c.tableHeadCell}>{i18n.t("aeros.queues.table.bot")}</div>
            <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
              {i18n.t("aeros.queues.table.actions")}
            </div>
          </div>

          {loading && queues.length === 0 ? (
            <div className={c.loadingRow}>…</div>
          ) : visiveis.length === 0 ? (
            <div className={c.emptyState}>
              <span className={c.emptyIcon}>
                <AccountTreeIcon />
              </span>
              <div className={c.emptyTitle}>{i18n.t("aeros.queues.empty.title")}</div>
              <div className={c.emptyText}>{i18n.t("aeros.queues.empty.text")}</div>
            </div>
          ) : (
            visiveis.map(renderRow)
          )}

          {visiveis.length > 0 && (
            <div className={classes.reorderHint}>
              <InfoOutlinedIcon />
              {i18n.t("aeros.queues.reorderHint")}
            </div>
          )}
        </div>
      </div>

      <QueueModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelected(null);
        }}
        queueId={selected?.id}
        userCount={selected?.userCount || 0}
        onSaved={carregar}
      />

      <AerosConfirmModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        loading={deleting}
        icon={<DeleteForeverIcon />}
        title={i18n.t("aeros.queues.delete.title", { name: selected?.name || "" })}
        description={deleteDescription()}
        confirmLabel={i18n.t("aeros.queues.delete.confirm")}
      />
    </MainContainer>
  );
};

export default Queues;
